import React, { useEffect, useMemo } from "react";
import Hammer from "hammerjs";

import { checkIfIOSDevice } from "src/shared/utils";

export const DRAGPANEL_TOP_THRESHOLD = 75;
const useDragger = ({
  dragPanel,
  shouldPerformTouchGesture,
  onPanStart,
  onPanMove,
  onPanEnd,
}: {
  dragPanel: React.MutableRefObject<HTMLElement>;
  shouldPerformTouchGesture?: (e: HammerInput) => boolean;
  onPanStart?: (e: HammerInput, isDockedUp: boolean) => void;
  onPanMove?: (e: HammerInput, isDockedUp: boolean) => void;
  onPanEnd?: (e: HammerInput, isDockedUp: boolean) => void;
}) => {
  const isIOSDevice = useMemo(() => checkIfIOSDevice(), []);
  useEffect(() => {
    const el = dragPanel.current;
    if (!el) return;

    let isDockedUp = false;
    const upperLimit = el.getBoundingClientRect().top - DRAGPANEL_TOP_THRESHOLD;
    const hammerOptions = isIOSDevice ? { touchAction: "pan-y", domEvents: true } : { domEvents: true };
    const hammered = new Hammer(el, hammerOptions);
    hammered.get("pan").set({
      direction: Hammer.DIRECTION_VERTICAL,
      threshold: 0,
    });

    const handlePanStart: HammerListener = (e) => {
      e.srcEvent.stopPropagation();
      if (checkIfIOSDevice()) {
        document.body.style["overscroll-behavior"] = "contain";
        document.body.style.overflowY = "hidden";
      }
      if (shouldPerformTouchGesture && !shouldPerformTouchGesture(e)) return;
      // e.srcEvent.preventDefault();
      onPanStart && onPanStart(e, isDockedUp);

      //   el.style.transition = "";
    };

    const handlePanMove: HammerListener = (e) => {
      e.srcEvent.stopPropagation();
      if (shouldPerformTouchGesture && !shouldPerformTouchGesture(e)) return;
      e.srcEvent.preventDefault();
      onPanMove && onPanMove(e, isDockedUp);

      const distance = e.distance;
      // console.warn(distance, upperLimit, e);
      if (Math.abs(distance) < Math.abs(upperLimit)) {
        if (isDockedUp && e.direction === Hammer.DIRECTION_UP && e.deltaY < 0) {
          return;
        }
        const movement = isDockedUp ? -(upperLimit - distance) : e.deltaY;
        el.style.transform = `translateY(${movement}px)`;
      }
    };

    const handlePanEnd: HammerListener = (e) => {
      if (checkIfIOSDevice()) {
        document.body.style["overscroll-behavior"] = "auto";
        document.body.style.overflowY = "auto";
      }
      if (shouldPerformTouchGesture && !shouldPerformTouchGesture(e)) return;
      //   el.style.transition = "transform 0.1s ease-out";

      if (e.distance > Math.abs(upperLimit / 3)) {
        el.style.transform = `translateY(${isDockedUp ? 0 : -upperLimit}px)`;
        isDockedUp = !isDockedUp;
      } else {
        el.style.transform = `translateY(${isDockedUp ? -upperLimit : 0}px)`;
      }
      onPanEnd && onPanEnd(e, isDockedUp);
    };

    hammered.on("panstart", handlePanStart);
    hammered.on("panup pandown", handlePanMove);
    hammered.on("panend pancancel", handlePanEnd);

    return () => {
      hammered.off("panstart", handlePanStart);
      hammered.off("panup pandown", handlePanMove);
      hammered.off("panend pancancel", handlePanEnd);
    };
  }, [dragPanel.current]);
};

export default useDragger;
